<template>
  <div class="ec2-codex-page">
    <div class="page-header">
      <div>
        <div class="text-overline">Fulladmin</div>
        <h1>EC2 Codex</h1>
        <p>Controle da maquina e conversa remota com o mesmo Codex da EC2 ou do notebook.</p>
      </div>
      <div class="header-actions">
        <v-btn outlined color="primary" :loading="loading" @click="loadAll">
          <v-icon left>mdi-refresh</v-icon>
          Atualizar
        </v-btn>
      </div>
    </div>

    <v-alert v-if="error" type="error" outlined dense class="mb-4">{{ error }}</v-alert>
    <v-alert v-if="remoteError" type="error" outlined dense class="mb-4">{{ remoteError }}</v-alert>
    <v-alert v-if="message" type="success" outlined dense class="mb-4">{{ message }}</v-alert>

    <v-tabs v-model="activeTab" show-arrows class="mb-4" aria-label="Áreas do Codex remoto">
      <v-tab>Conversar</v-tab>
      <v-tab>Tarefas</v-tab>
      <v-tab>Conversas</v-tab>
      <v-tab>Máquina EC2</v-tab>
      <v-tab>WhatsApp</v-tab>
    </v-tabs>
    <v-tabs-items v-model="activeTab">
      <v-tab-item>
    <v-card outlined>
      <v-card-title class="panel-title"><v-icon left>mdi-robot</v-icon>Codex remoto</v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="4">
            <v-select v-model="selectedHostId" :items="hostOptions" item-text="label" item-value="id" label="Máquina do Codex" outlined dense @change="changeHost" />
            <v-chip x-small :color="selectedHostOnline ? 'success' : 'grey'" dark>{{ selectedHostOnline ? "agente online" : "agente offline" }}</v-chip>
            <p class="help-text mt-3">As tarefas usam o Codex da máquina escolhida.</p>
            <v-btn block outlined color="primary" :loading="loadingThreads" :disabled="!selectedHostOnline" @click="syncThreads"><v-icon left>mdi-history</v-icon>Atualizar conversas</v-btn>
          </v-col>
          <v-col cols="12" md="8">
            <v-select v-model="selectedThreadId" :items="threads" item-text="label" item-value="threadId" label="Conversa existente (opcional)" outlined dense clearable hint="Vazio cria uma nova conversa do Codex" persistent-hint />
            <v-text-field v-model="taskTitle" label="Nome da tarefa" outlined dense maxlength="160" counter="160" :disabled="sending" />
            <v-textarea v-model="promptText" label="Mensagem ou comando" outlined auto-grow rows="3" :disabled="sending" hint="Digite /ajuda para comandos do painel ou !git status para executar na máquina" persistent-hint />
            <v-file-input v-model="pendingFiles" multiple chips show-size outlined dense prepend-icon="mdi-paperclip" label="Fotos e arquivos (até 5, 10 MB cada)" :disabled="sending" @change="validateFiles" />
            <div class="action-grid">
              <v-btn color="primary" :loading="sending" :disabled="(!promptText.trim() && !pendingFiles.length) || !selectedHostOnline" @click="runInput"><v-icon left>mdi-send</v-icon>Enviar ao Codex</v-btn>
              <v-btn outlined @click="newThread"><v-icon left>mdi-plus</v-icon>Nova conversa</v-btn>
              <v-btn outlined :disabled="!selectedThreadId || !selectedHostOnline" :loading="loadingHistory" @click="readHistory"><v-icon left>mdi-text-box-search-outline</v-icon>Ver histórico</v-btn>
              <v-btn outlined :disabled="!selectedThreadId" @click="interrupt"><v-icon left>mdi-stop-circle-outline</v-icon>Interromper tarefa</v-btn>
            </div>
            <div v-if="showHelp" class="command-help mt-4"><strong>Comandos:</strong> /ajuda, /status, /conversas, /historico, /interromper e /nova. Prefixe ! para executar na máquina selecionada. Qualquer outro texto é enviado ao Codex.</div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
      </v-tab-item>
      <v-tab-item>
        <v-card outlined>
          <v-card-title class="panel-title"><v-icon left>mdi-format-list-bulleted</v-icon>Tarefas do Codex</v-card-title>
          <v-card-text>
            <p class="help-text">Tarefas do painel e do WhatsApp. Atualização automática a cada 10 segundos.</p>
            <v-row dense><v-col cols="12" md="5"><v-text-field v-model="taskSearch" prepend-inner-icon="mdi-magnify" label="Buscar tarefa ou resposta" outlined dense clearable /></v-col><v-col cols="6" md="3"><v-select v-model="taskHostFilter" :items="[{ id: '', label: 'Todas as máquinas' }, ...hostOptions]" item-text="label" item-value="id" label="Máquina" outlined dense /></v-col><v-col cols="6" md="4"><v-select v-model="taskStatusFilter" :items="taskStatusOptions" item-text="label" item-value="id" label="Estado" outlined dense /></v-col></v-row>
            <v-simple-table dense class="task-table"><thead><tr><th>Tarefa</th><th>Máquina</th><th>Estado</th><th>Origem</th><th>Resumo</th><th></th></tr></thead><tbody>
              <tr v-for="command in filteredCommands" :key="command.id"><td><strong>{{ commandTitle(command) }}</strong><div class="caption">#{{ command.id }}</div></td><td>{{ hostLabel(command.hostId) }}</td><td><v-chip x-small :color="commandColor(command.status)" dark>{{ statusLabel(command.status) }}</v-chip></td><td>{{ sourceLabel(command.source) }}</td><td class="result-cell">{{ commandSummary(command) }}</td><td><v-btn small text color="primary" @click="openCommand(command)">Acompanhar</v-btn></td></tr>
              <tr v-if="!filteredCommands.length"><td colspan="6" class="text-center grey--text">Nenhuma tarefa encontrada.</td></tr>
            </tbody></v-simple-table>
            <section v-if="selectedCommand" class="task-detail mt-4">
              <div class="detail-heading"><h3>{{ commandTitle(selectedCommand) }}</h3><v-btn small text @click="closeCommand">Fechar</v-btn></div>
              <v-chip x-small :color="commandColor(selectedCommand.status)" dark>{{ statusLabel(selectedCommand.status) }}</v-chip>
              <v-btn v-if="selectedCommand.status === 'running' && selectedCommand.threadId" small text color="error" @click="interruptCommand(selectedCommand)">Interromper</v-btn>
              <h4 class="mt-4">Resposta do Codex</h4>
              <div class="response-text">{{ selectedCommand.resultText || selectedCommand.errorText || "O Codex ainda está trabalhando. A atividade aparece abaixo." }}</div>
              <h4 v-if="taskFiles.length" class="mt-4">Arquivos e fotos</h4>
              <div v-for="file in taskFiles" :key="file.id" class="file-row"><v-icon small>mdi-paperclip</v-icon><span>{{ file.fileName }} ({{ formatBytes(file.sizeBytes) }})</span><v-btn small text color="primary" @click="viewFile(file)">Ver</v-btn><v-btn small text color="primary" @click="downloadFile(file)">Baixar</v-btn></div>
              <h4 class="mt-4">Acompanhamento</h4>
              <div v-for="(entry, index) in friendlyEntries" :key="index" class="activity-entry"><strong>{{ entry.label }}</strong><div>{{ entry.text }}</div></div>
              <p v-if="!friendlyEntries.length" class="help-text">Aguardando atividade do agente...</p>
              <details v-if="technicalEntries.length" class="mt-4"><summary>Mostrar detalhes técnicos</summary><div v-for="(entry, index) in technicalEntries" :key="index" class="technical-entry"><strong>{{ entry.label }}</strong><pre>{{ entry.text }}</pre></div></details>
            </section>
          </v-card-text>
        </v-card>
      </v-tab-item>
      <v-tab-item>
        <v-card outlined><v-card-title class="panel-title"><v-icon left>mdi-message-text-outline</v-icon>Conversas em todas as máquinas</v-card-title><v-card-text>
          <v-row dense><v-col cols="12" md="6"><v-text-field v-model="threadSearch" prepend-inner-icon="mdi-magnify" label="Buscar conversa" outlined dense clearable /></v-col><v-col cols="12" md="6"><v-select v-model="threadHostFilter" :items="[{ id: '', label: 'Todas as máquinas' }, ...hostOptions]" item-text="label" item-value="id" label="Máquina" outlined dense /></v-col></v-row>
          <v-simple-table dense class="task-table"><thead><tr><th>Conversa</th><th>Máquina</th><th>Estado</th><th>Atualizada</th><th></th></tr></thead><tbody>
            <tr v-for="thread in filteredThreads" :key="thread.hostId + ':' + thread.threadId"><td><strong>{{ thread.label }}</strong></td><td>{{ hostLabel(thread.hostId) }}</td><td><v-chip x-small :color="threadColor(thread.status)" dark>{{ threadStatusLabel(thread.status) }}</v-chip></td><td>{{ formatThreadDate(thread.updatedAtUnix) }}</td><td><v-btn small text color="primary" :disabled="!hostOnline(thread.hostId)" :loading="loadingHistory && historyThreadId === thread.threadId" @click="readHistory(thread)">Ver histórico</v-btn><v-btn small text color="primary" :disabled="!hostOnline(thread.hostId)" @click="continueThread(thread)">Continuar</v-btn></td></tr>
            <tr v-if="!filteredThreads.length"><td colspan="5" class="text-center grey--text">Nenhuma conversa encontrada.</td></tr>
          </tbody></v-simple-table>
          <section v-if="historyThreadId && !loadingHistory" class="task-detail mt-4"><div class="detail-heading"><h3>{{ historyTitle }}</h3><v-btn small text @click="clearHistory">Fechar</v-btn></div>
            <div v-for="(entry, index) in historyMessages" :key="index" class="message-bubble" :class="entry.role"><strong>{{ entry.role === 'user' ? 'Você' : 'Codex' }}</strong><div>{{ entry.text }}</div></div>
            <p v-if="!historyMessages.length" class="help-text">Ainda não há mensagens nessa conversa.</p>
            <h4 v-if="historyFiles.length" class="mt-4">Arquivos e fotos</h4><div v-for="file in historyFiles" :key="file.id" class="file-row"><v-icon small>mdi-paperclip</v-icon><span>{{ file.fileName }} ({{ formatBytes(file.sizeBytes) }})</span><v-btn small text color="primary" @click="viewFile(file)">Ver</v-btn><v-btn small text color="primary" @click="downloadFile(file)">Baixar</v-btn></div>
          </section>
        </v-card-text></v-card>
      </v-tab-item>
      <v-tab-item>
        <v-tabs v-model="activeMachineTab" show-arrows class="mb-4" aria-label="Máquinas EC2">
          <v-tab>Codex EC2</v-tab>
          <v-tab>Servidor Slide1 (backups)</v-tab>
          <v-tab>quentora-pilot</v-tab>
        </v-tabs>
        <v-tabs-items v-model="activeMachineTab">
          <v-tab-item><ec2-machine-card ref="codexMachine" machine-key="codex" title="Codex EC2" :agent-label="ec2AgentLabel" /></v-tab-item>
          <v-tab-item><ec2-machine-card ref="backupMachine" machine-key="backup" title="Servidor Slide1 (backups)" schedule="Início automático às sextas-feiras, 22h; desligamento aos domingos, 22h (horário de Brasília)." /></v-tab-item>
          <v-tab-item><ec2-machine-card ref="quentoraMachine" machine-key="quentora" title="quentora-pilot" :has-rdp="false" /></v-tab-item>
        </v-tabs-items>
      </v-tab-item>
      <v-tab-item>
    <v-card outlined class="mt-4">
      <v-card-title class="panel-title"><v-icon left>mdi-whatsapp</v-icon>WhatsApp do Codex</v-card-title>
      <v-card-text>
        <v-row align="center">
          <v-col cols="12" md="8">
            <p class="mb-2">Vincule o WhatsApp Business já usado pela Slide1 sem desconectar o aplicativo do celular.</p>
            <div class="agent-state"><v-chip x-small :color="whatsAppStatus.connected ? 'success' : 'grey'" dark>{{ whatsAppStatus.connected ? "número conectado" : "número ainda não conectado" }}</v-chip></div>
            <p v-if="whatsAppStatus.connected" class="help-text mb-0">{{ whatsAppStatus.displayPhoneNumber || "Número configurado" }}<span v-if="whatsAppStatus.tokenExpiresAt"> · acesso válido até {{ formatDate(whatsAppStatus.tokenExpiresAt) }}</span></p>
            <p v-else class="help-text mb-0">A vinculação abre o fluxo oficial da Meta e guarda o acesso somente no servidor.</p>
            <v-alert v-if="loadingWhatsApp" type="info" outlined dense class="mt-3 mb-0">Aguardando a Meta. Se a janela não aparecer, permita pop-ups para este site e tente novamente. <v-btn small text color="info" :disabled="!cancelWhatsAppFlow" @click="cancelWhatsAppFlow">Cancelar tentativa</v-btn></v-alert>
            <v-alert v-if="whatsAppSetupError" type="error" outlined dense class="mt-3 mb-0">{{ whatsAppSetupError }} <v-btn small text color="error" @click="prepareWhatsAppSignup">Tentar novamente</v-btn></v-alert>
          </v-col>
          <v-col cols="12" md="4" class="text-md-right">
            <v-btn color="success" :loading="preparingWhatsApp || loadingWhatsApp" :disabled="!whatsAppReady" @click="startWhatsAppCoexistence"><v-icon left>mdi-link-variant</v-icon>{{ whatsAppStatus.connected ? "Reconectar WhatsApp" : "Vincular WhatsApp Business" }}</v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

      </v-tab-item>
    </v-tabs-items>
  </div>
</template>

<script>
import { ec2CodexAPI } from "../API/Ec2CodexAPI";
import Ec2MachineCard from "./Ec2MachineCard.vue";

export default {
  name: "Ec2CodexPanel",
  components: { Ec2MachineCard },
  data() {
    return {
      hosts: [], threads: [], allThreads: [], commands: [], events: [], error: "", remoteError: "", message: "", whatsAppStatus: {},
      activeTab: 0, activeMachineTab: 0, taskSearch: "", taskHostFilter: "", taskStatusFilter: "", threadSearch: "", threadHostFilter: "", pendingFiles: [], taskFiles: [], historyFiles: [], historyMessages: [],
      whatsAppConfiguration: null, whatsAppReady: false, whatsAppSetupError: "", preparingWhatsApp: false, cancelWhatsAppFlow: null,
      selectedHostId: "ec2", selectedThreadId: null, selectedCommandId: null, commandDetail: null, promptText: "", taskTitle: "",
      showHelp: false, detailFullScreen: false, historyText: "", historyTitle: "", historyThreadId: null, historyExpanded: false,
      syncCommands: {}, lastSyncAt: {},
      loading: false,
      loadingThreads: false, loadingHistory: false, sending: false, loadingWhatsApp: false, pollTimer: null,
    };
  },
  computed: {
    hostOptions() {
      const known = [{ id: "ec2", label: "Codex EC2" }, { id: "notebook", label: "Codex Notebook" }];
      this.hosts.forEach((host) => {
        if (!known.some((option) => option.id === host.hostId)) known.push({ id: host.hostId, label: host.displayName || host.hostId });
      });
      return known;
    },
    selectedHost() { return this.hosts.find((host) => host.hostId === this.selectedHostId); },
    selectedThread() { return this.threads.find((thread) => thread.threadId === this.selectedThreadId); },
    selectedHostOnline() { return Boolean(this.selectedHost && this.selectedHost.online); },
    ec2AgentLabel() { const host = this.hosts.find((item) => item.hostId === "ec2"); return host && host.online ? "online" : "offline"; },
    selectedCommand() { return (this.commandDetail && this.commandDetail.id === this.selectedCommandId) ? this.commandDetail : this.commands.find((item) => item.id === this.selectedCommandId); },
    taskStatusOptions() { return [{ id: "", label: "Todos os estados" }, { id: "running", label: "Em andamento" }, { id: "queued", label: "Na fila" }, { id: "completed", label: "Concluídas" }, { id: "failed", label: "Falhas" }]; },
    filteredCommands() {
      const query = (this.taskSearch || "").toLocaleLowerCase("pt-BR").trim();
      return this.commands.filter((item) => ["prompt", "shell_command"].includes(item.kind)
        && (!this.taskHostFilter || item.hostId === this.taskHostFilter)
        && (!this.taskStatusFilter || item.status === this.taskStatusFilter)
        && (!query || `${this.commandTitle(item)} ${this.commandSummary(item)} ${item.id}`.toLocaleLowerCase("pt-BR").includes(query)));
    },
    filteredThreads() {
      const query = (this.threadSearch || "").toLocaleLowerCase("pt-BR").trim();
      return this.allThreads.filter((item) => (!this.threadHostFilter || item.hostId === this.threadHostFilter)
        && (!query || `${item.label} ${item.preview || ""} ${item.threadId}`.toLocaleLowerCase("pt-BR").includes(query)));
    },
    friendlyEntries() { return this.terminalEntries.filter((item) => ["Codex", "Codex escrevendo", "Erro", "Aviso"].includes(item.label)); },
    technicalEntries() { return this.terminalEntries.filter((item) => !["Codex", "Codex escrevendo", "Erro", "Aviso"].includes(item.label)); },
    terminalEntries() {
      const entries = [];
      let draft = "";
      let commandDraft = "";
      this.events.forEach((event) => {
        if (event.eventType === "item/agentMessage/delta") { draft += this.eventPayload(event).delta || event.message || ""; return; }
        if (event.eventType === "item/commandExecution/outputDelta") {
          commandDraft += this.eventPayload(event).delta || event.message || "";
          return;
        }
        if (event.eventType === "item/completed") {
          const payload = this.eventPayload(event);
          const item = payload.item || {};
          if (item.type === "agentMessage") {
            entries.push({ label: "Codex", text: item.text || draft }); draft = "";
          } else if (item.type === "commandExecution") {
            entries.push({ label: "Comando", text: `${item.command || ""}\n${item.aggregatedOutput || commandDraft}`.trim() });
            commandDraft = "";
          }
          return;
        }
        if (event.eventType === "item/started") {
          const item = this.eventPayload(event).item || {};
          if (item.type === "commandExecution") entries.push({ label: "Executando", text: item.command || event.message });
          return;
        }
        const labels = { started: "Agente", thread: "Conversa", "turn/started": "Codex", "turn/completed": "Estado", failed: "Erro", warning: "Aviso" };
        if (labels[event.eventType]) entries.push({ label: labels[event.eventType], text: event.message || event.eventType });
      });
      if (draft) entries.push({ label: "Codex escrevendo", text: draft });
      if (commandDraft) entries.push({ label: "Saída em andamento", text: commandDraft });
      return entries;
    },
  },
  mounted() {
    this.loadAll().then(() => this.autoSyncThreads());
    this.prepareWhatsAppSignup();
    this.pollTimer = window.setInterval(async () => { await this.loadAll(true); await this.autoSyncThreads(); }, 10000);
  },
  beforeDestroy() { if (this.pollTimer) window.clearInterval(this.pollTimer); },
  methods: {
    async loadAll(silent = false) {
      this.loading = !silent;
      if (!silent) this.error = "";
      await Promise.all([this.loadMachineStatus(), this.loadRemoteState()]);
      if (this.selectedCommandId) await Promise.all([this.loadCommandDetail(this.selectedCommandId), this.loadEvents(this.selectedCommandId)]);
      this.loading = false;
    },
    async loadMachineStatus() {
      await Promise.all([this.$refs.codexMachine, this.$refs.backupMachine, this.$refs.quentoraMachine].filter(Boolean).map((card) => card.refresh()));
    },
    async loadRemoteState() {
      try {
        const [hosts, commands] = await Promise.all([ec2CodexAPI.hosts(), ec2CodexAPI.commands(null)]);
        this.hosts = hosts.data || [];
        this.commands = commands.data || [];
        this.remoteError = "";
        await this.refreshThreads(true);
      } catch (error) { this.remoteError = this.extractError(error, "Não foi possível consultar os agentes remotos."); }
    },
    async refreshThreads(silent = false) {
      this.loadingThreads = !silent;
      try {
        const hosts = this.hostOptions.map((item) => item.id);
        const results = await Promise.all(hosts.map((hostId) => ec2CodexAPI.threads(hostId)));
        this.allThreads = results.reduce((list, result) => list.concat((result.data || []).map((thread) => Object.assign({}, thread, { label: thread.name || thread.preview || thread.threadId }))), [])
          .sort((a, b) => (b.updatedAtUnix || b.createdAtUnix || 0) - (a.updatedAtUnix || a.createdAtUnix || 0));
        this.threads = this.allThreads.filter((thread) => thread.hostId === this.selectedHostId);
      } catch (error) { this.remoteError = this.extractError(error, "Não foi possível consultar as conversas remotas."); }
      finally { if (!silent) this.loadingThreads = false; }
    },
    async syncThreads() {
      const hostId = this.selectedHostId;
      this.loadingThreads = true;
      this.remoteError = "";
      try {
        const response = await ec2CodexAPI.command({ hostId, kind: "list_threads" });
        const commandId = response.data.commandId;
        for (let attempt = 0; attempt < 30; attempt += 1) {
          await new Promise((resolve) => window.setTimeout(resolve, 1000));
          const command = (await ec2CodexAPI.commandStatus(commandId)).data;
          if (command && command.status === "failed") throw new Error(command.errorText || "O agente não conseguiu atualizar as conversas.");
          if (command && command.status === "completed") {
            await this.refreshThreads(true);
            return;
          }
        }
        throw new Error("O agente demorou para atualizar as conversas. Tente novamente.");
      } catch (error) { this.remoteError = this.extractError(error, "Não foi possível atualizar as conversas."); }
      finally { this.loadingThreads = false; }
    },
    async autoSyncThreads() {
      for (const host of this.hosts.filter((item) => item.online)) {
        try {
          const pendingId = this.syncCommands[host.hostId];
          if (pendingId) {
            const command = (await ec2CodexAPI.commandStatus(pendingId)).data;
            if (command && ["completed", "failed"].includes(command.status)) {
              this.$delete(this.syncCommands, host.hostId);
              if (command.status === "completed") await this.refreshThreads(true);
            }
            continue;
          }
          if (Date.now() - (this.lastSyncAt[host.hostId] || 0) < 300000) continue;
          const response = await ec2CodexAPI.command({ hostId: host.hostId, kind: "list_threads" });
          this.$set(this.syncCommands, host.hostId, response.data.commandId);
          this.$set(this.lastSyncAt, host.hostId, Date.now());
        } catch (error) { this.remoteError = this.extractError(error, "Não foi possível sincronizar as conversas."); }
      }
    },
    async prepareWhatsAppSignup() {
      this.preparingWhatsApp = true;
      this.whatsAppReady = false;
      this.whatsAppSetupError = "";
      try {
        const response = await ec2CodexAPI.whatsAppEmbeddedSignup();
        const configuration = response.data && response.data.configuration;
        if (!configuration || !configuration.appId || !configuration.configurationId) throw new Error("A configuração do WhatsApp não foi encontrada.");
        this.whatsAppConfiguration = configuration;
        this.whatsAppStatus = (response.data && response.data.status) || {};
        await this.loadFacebookSdk(configuration.appId);
        this.whatsAppReady = true;
      } catch (error) { this.whatsAppSetupError = this.extractError(error, "Não foi possível preparar o vínculo com a Meta."); }
      finally { this.preparingWhatsApp = false; }
    },
    async changeHost() { this.selectedThreadId = null; await this.loadAll(); },
    async sendPrompt() {
      this.sending = true; this.error = "";
      try {
        this.validateFiles(this.pendingFiles);
        if (this.error) return;
        const uploaded = await Promise.all(this.pendingFiles.map((file) => ec2CodexAPI.uploadFile(this.selectedHostId, this.selectedThreadId, file)));
        const response = await ec2CodexAPI.prompt({ hostId: this.selectedHostId, threadId: this.selectedThreadId, title: this.taskTitle.trim(), text: this.promptText.trim() || "Analise os arquivos enviados.", cwd: this.selectedThread && this.selectedThread.cwd, fileIds: uploaded.map((item) => item.data.id) });
        this.message = `Tarefa #${response.data.commandId} enviada para ${this.selectedHostId}.`;
        this.promptText = ""; this.taskTitle = ""; this.pendingFiles = []; this.selectedCommandId = response.data.commandId; this.activeTab = 1;
        await this.loadAll(true);
        await this.loadCommandDetail(response.data.commandId);
        await this.loadFiles(this.selectedHostId, this.selectedThreadId, response.data.commandId, "taskFiles");
      } catch (error) { this.error = this.extractError(error); } finally { this.sending = false; }
    },
    async runInput() {
      const input = this.promptText.trim();
      if (!input && this.pendingFiles.length) { await this.sendPrompt(); return; }
      const actions = {
        "/ajuda": () => { this.showHelp = !this.showHelp; },
        "/status": () => this.loadAll(),
        "/conversas": () => this.syncThreads(),
        "/historico": () => this.readHistory(),
        "/interromper": () => this.interrupt(),
        "/nova": () => this.newThread(),
      };
      if (actions[input.toLowerCase()] && !this.pendingFiles.length) {
        await actions[input.toLowerCase()]();
        this.promptText = "";
      } else if (input.startsWith("/") && !this.pendingFiles.length) {
        this.showHelp = true;
        this.error = "Comando desconhecido. Consulte /ajuda.";
      } else if (input.startsWith("!") && !this.pendingFiles.length) {
        await this.sendShellCommand(input.slice(1).trim());
      } else await this.sendPrompt();
    },
    async sendShellCommand(commandText) {
      if (!commandText) { this.error = "Digite o comando após !."; return; }
      this.sending = true; this.error = "";
      try {
        const response = await ec2CodexAPI.command({ hostId: this.selectedHostId, kind: "shell_command", threadId: this.selectedThreadId,
          cwd: this.selectedThread && this.selectedThread.cwd, title: this.taskTitle.trim(), text: commandText });
        this.message = `Comando #${response.data.commandId} enviado para ${this.selectedHostId}.`;
        this.promptText = ""; this.taskTitle = ""; this.selectedCommandId = response.data.commandId;
        await this.loadAll(true);
      } catch (error) { this.error = this.extractError(error); }
      finally { this.sending = false; }
    },
    async startWhatsAppCoexistence() {
      this.loadingWhatsApp = true; this.whatsAppSetupError = "";
      try {
        if (!this.whatsAppReady || !this.whatsAppConfiguration || !window.FB) throw new Error("O vínculo com a Meta ainda não está pronto. Tente novamente.");
        // FB.login must run during the click gesture so browsers allow its popup.
        const signup = await this.runWhatsAppEmbeddedSignup(this.whatsAppConfiguration.configurationId);
        const saved = await ec2CodexAPI.completeWhatsAppEmbeddedSignup(signup);
        this.whatsAppStatus = saved.data || {};
        this.message = "WhatsApp Business conectado ao Codex.";
      } catch (error) { this.whatsAppSetupError = this.extractError(error); }
      finally { this.cancelWhatsAppFlow = null; this.loadingWhatsApp = false; }
    },
    loadFacebookSdk(appId) {
      if (window.FB) {
        window.FB.init({ appId, cookie: true, xfbml: false, version: "v23.0" });
        return Promise.resolve();
      }
      return new Promise((resolve, reject) => {
        const existing = document.getElementById("facebook-jssdk");
        if (existing) {
          existing.addEventListener("load", () => { window.FB.init({ appId, cookie: true, xfbml: false, version: "v23.0" }); resolve(); }, { once: true });
          existing.addEventListener("error", () => reject(new Error("Nao foi possivel carregar o login da Meta.")), { once: true });
          return;
        }
        const script = document.createElement("script");
        script.id = "facebook-jssdk"; script.async = true; script.defer = true; script.src = "https://connect.facebook.net/pt_BR/sdk.js";
        script.onload = () => { window.FB.init({ appId, cookie: true, xfbml: false, version: "v23.0" }); resolve(); };
        script.onerror = () => reject(new Error("Nao foi possivel carregar o login da Meta."));
        document.head.appendChild(script);
      });
    },
    runWhatsAppEmbeddedSignup(configurationId) {
      return new Promise((resolve, reject) => {
        let authorizationCode = null; let session = null;
        let timeout = window.setTimeout(() => { cleanup(); reject(new Error("A Meta não concluiu a vinculação. Verifique se o navegador bloqueou a janela e tente novamente.")); }, 600000);
        const cleanup = () => { window.removeEventListener("message", onMessage); if (timeout) window.clearTimeout(timeout); };
        this.cancelWhatsAppFlow = () => { cleanup(); reject(new Error("Tentativa cancelada. Permita pop-ups para este site e tente novamente.")); };
        const finish = () => {
          if (!authorizationCode || !session) return;
          cleanup();
          resolve({ code: authorizationCode, wabaId: session.waba_id, phoneNumberId: session.phone_number_id });
        };
        const onMessage = (event) => {
          if (!["https://www.facebook.com", "https://web.facebook.com", "https://business.facebook.com"].includes(event.origin) || typeof event.data !== "string") return;
          try {
            const data = JSON.parse(event.data);
            if (data.type === "WA_EMBEDDED_SIGNUP" && ["FINISH", "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING"].includes(data.event)) { session = data.data; finish(); }
            if (data.type === "WA_EMBEDDED_SIGNUP" && (data.event === "CANCEL" || data.event === "ERROR")) { cleanup(); reject(new Error("A Meta cancelou ou não concluiu a vinculação do WhatsApp.")); }
          } catch (error) { /* mensagens de outras janelas nao fazem parte do cadastro */ }
        };
        window.addEventListener("message", onMessage);
        window.FB.login((response) => {
          authorizationCode = response && response.authResponse && response.authResponse.code;
          if (!authorizationCode) { cleanup(); reject(new Error("A Meta cancelou ou nao concluiu a vinculacao do WhatsApp.")); return; }
          finish();
        }, {
          config_id: configurationId,
          response_type: "code",
          override_default_response_type: true,
          extras: { setup: {}, featureType: "whatsapp_business_app_onboarding", sessionInfoVersion: "3" }
        });
      });
    },
    newThread() { this.selectedThreadId = null; this.promptText = ""; this.message = "A próxima mensagem iniciará uma nova conversa do Codex."; },
    async interrupt() {
      try { const response = await ec2CodexAPI.command({ hostId: this.selectedHostId, kind: "interrupt", threadId: this.selectedThreadId }); this.message = `Pedido de interrupcao enviado (#${response.data.commandId}).`; }
      catch (error) { this.error = this.extractError(error); }
    },
    async interruptCommand(command) {
      try {
        await ec2CodexAPI.command({ hostId: command.hostId, kind: "interrupt", threadId: command.threadId });
        this.message = "Pedido de interrupção enviado.";
      } catch (error) { this.error = this.extractError(error); }
    },
    async continueThread(thread) {
      this.selectedHostId = thread.hostId;
      await this.refreshThreads(true);
      this.selectedThreadId = thread.threadId;
      this.activeTab = 0;
      this.message = `Conversa “${thread.label}” selecionada. Envie uma nova mensagem acima.`;
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    async loadEvents(commandId) {
      try {
        const afterId = this.events.length ? this.events[this.events.length - 1].id : 0;
        const response = await ec2CodexAPI.events(commandId, afterId);
        if (this.selectedCommandId === commandId) {
          const known = new Set(this.events.map((event) => event.id));
          this.events = this.events.concat((response.data || []).filter((event) => !known.has(event.id)));
        }
      }
      catch (error) { this.error = this.extractError(error); }
    },
    async openCommand(command) {
      if (this.selectedCommandId === command.id) { this.selectedCommandId = null; this.commandDetail = null; this.events = []; return; }
      this.selectedCommandId = command.id;
      this.commandDetail = null;
      this.events = [];
      this.taskFiles = [];
      await Promise.all([this.loadCommandDetail(command.id), this.loadEvents(command.id), this.loadFiles(command.hostId, command.threadId, command.id, "taskFiles")]);
    },
    async loadCommandDetail(commandId) {
      try {
        const response = await ec2CodexAPI.commandStatus(commandId);
        if (this.selectedCommandId === commandId) this.commandDetail = response.data;
      } catch (error) { this.error = this.extractError(error); }
    },
    async readHistory(thread = null) {
      const hostId = thread ? thread.hostId : this.selectedHostId;
      const threadId = thread ? thread.threadId : this.selectedThreadId;
      if (!threadId) { this.error = "Selecione uma conversa para ler o histórico."; return; }
      this.loadingHistory = true; this.historyThreadId = threadId; this.error = "";
      this.historyText = "";
      this.historyMessages = []; this.historyFiles = [];
      this.historyTitle = thread ? thread.label : (this.selectedThread && this.selectedThread.label) || threadId;
      try {
        const response = await ec2CodexAPI.command({ hostId, kind: "read_thread", threadId });
        for (let attempt = 0; attempt < 60; attempt += 1) {
          await new Promise((resolve) => window.setTimeout(resolve, 1000));
          const command = (await ec2CodexAPI.commandStatus(response.data.commandId)).data;
          if (command.status === "failed") throw new Error(command.errorText || "Não foi possível ler a conversa.");
          if (command.status === "completed") {
            this.historyText = command.resultText || "";
            this.historyMessages = this.parseHistory(this.historyText);
            await this.loadFiles(hostId, threadId, null, "historyFiles");
            this.activeTab = 2;
            return;
          }
        }
        throw new Error("A leitura demorou. Aguarde a tarefa atual terminar e tente novamente.");
      } catch (error) { this.error = this.extractError(error, "Não foi possível ler o histórico."); }
      finally { this.loadingHistory = false; }
    },
    eventPayload(event) { try { return JSON.parse(event.payloadJson || "{}"); } catch (error) { return {}; } },
    parseHistory(raw) {
      try {
        const parsed = JSON.parse(raw);
        if (parsed && Array.isArray(parsed.messages)) return parsed.messages.filter((item) => ["user", "assistant"].includes(item.role) && item.text);
      } catch (error) { /* agent antigo: extrair apenas mensagens */ }
      return String(raw || "").split(/(?=^(?:Voce|Você|Codex|Comando):\s*$)/m).map((part) => {
        const match = part.match(/^(Voce|Você|Codex):\s*\n?([\s\S]*)/);
        return match ? { role: match[1] === "Codex" ? "assistant" : "user", text: match[2].trim() } : null;
      }).filter(Boolean);
    },
    validateFiles(files) {
      const selected = Array.isArray(files) ? files : (files ? [files] : []);
      if (selected.length > 5 || selected.some((file) => file.size > 10 * 1024 * 1024 || file.size === 0)) {
        this.error = "Escolha até 5 arquivos, com no máximo 10 MB cada.";
        this.pendingFiles = [];
      } else if (this.error && this.error.startsWith("Escolha até 5 arquivos")) this.error = "";
    },
    async loadFiles(hostId, threadId, commandId, target) {
      try { const response = await ec2CodexAPI.files(hostId, threadId, commandId); this[target] = response.data || []; }
      catch (error) { this.error = this.extractError(error, "Não foi possível listar os arquivos."); }
    },
    async fileBlob(file) { return (await ec2CodexAPI.downloadFile(file.id)).data; },
    async downloadFile(file) {
      try {
        const blob = await this.fileBlob(file);
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a"); link.href = url; link.download = file.fileName;
        document.body.appendChild(link); link.click(); link.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 30000);
      } catch (error) { this.error = this.extractError(error, "Não foi possível baixar o arquivo."); }
    },
    async viewFile(file) {
      if (!["image/png", "image/jpeg", "image/webp", "image/gif", "application/pdf"].includes(file.contentType)) { await this.downloadFile(file); return; }
      try { const blob = await this.fileBlob(file); const url = URL.createObjectURL(blob); window.open(url, "_blank", "noopener"); window.setTimeout(() => URL.revokeObjectURL(url), 60000); }
      catch (error) { this.error = this.extractError(error, "Não foi possível visualizar o arquivo."); }
    },
    formatBytes(size) { return size < 1024 * 1024 ? `${Math.ceil(size / 1024)} KB` : `${(size / 1024 / 1024).toFixed(1)} MB`; },
    closeCommand() { this.selectedCommandId = null; this.commandDetail = null; this.events = []; this.taskFiles = []; },
    clearHistory() { this.historyThreadId = null; this.historyMessages = []; this.historyFiles = []; this.historyText = ""; },
    commandSummary(command) {
      if (command.kind === "read_thread") return "Histórico atualizado";
      return command.resultText || command.errorText || "Aguardando resposta...";
    },
    commandTitle(command) {
      const payload = command.payload || this.eventPayload({ payloadJson: command.payloadJson });
      const title = payload && payload.title;
      if (title) return title;
      const firstLine = payload && payload.text && payload.text.split("\n")[0].trim();
      if (firstLine) return firstLine.slice(0, 90);
      if (payload && payload.commandText) return `Comando: ${payload.commandText.slice(0, 80)}`;
      const thread = this.allThreads.find((item) => item.hostId === command.hostId && item.threadId === command.threadId);
      if (thread && thread.name) return thread.name;
      return `Tarefa #${command.id}`;
    },
    hostLabel(hostId) { const option = this.hostOptions.find((item) => item.id === hostId); return option ? option.label : hostId; },
    hostOnline(hostId) { const host = this.hosts.find((item) => item.hostId === hostId); return Boolean(host && host.online); },
    sourceLabel(source) { return ({ panel: "Painel", whatsapp: "WhatsApp" })[source] || source || "-"; },
    statusLabel(status) { return ({ queued: "Na fila", running: "Em andamento", completed: "Concluída", failed: "Falhou" })[status] || status || "-"; },
    threadStatusLabel(status) { return ({ active: "Em andamento", running: "Em andamento", completed: "Concluída", failed: "Falhou", interrupted: "Interrompida", idle: "Disponível", notLoaded: "Disponível", systemError: "Erro" })[status] || status || "-"; },
    threadColor(status) { return ({ active: "primary", running: "primary", completed: "success", failed: "error", interrupted: "warning", systemError: "error" })[status] || "grey"; },
    formatThreadDate(value) { return value ? new Date(value * 1000).toLocaleString("pt-BR") : "-"; },
    formatDate(value) { try { return new Date(value).toLocaleDateString("pt-BR"); } catch (error) { return value; } },
    commandColor(status) { return ({ completed: "success", running: "primary", queued: "warning", failed: "error" })[status] || "grey"; },
    extractError(error, fallback = "Não foi possível executar a operação.") {
      const data = error && error.response && error.response.data;
      if (data && data.mensagem) return data.mensagem;
      if (typeof data === "string" && data.trim() && !data.trim().startsWith("<")) return data.slice(0, 500);
      if (error && error.response) return `${fallback} (HTTP ${error.response.status}).`;
      return (error && error.message) || fallback;
    },
  },
};
</script>

<style scoped>
.ec2-codex-page { padding: 24px; }
.page-header { align-items: flex-start; display: flex; gap: 16px; justify-content: space-between; margin-bottom: 20px; }
.page-header h1 { font-size: 28px; font-weight: 600; line-height: 1.2; margin: 0; }
.page-header p { color: #5f6368; margin: 6px 0 0; }
.header-actions, .action-grid { display: flex; flex-wrap: wrap; gap: 10px; }
.panel-title { font-size: 16px; font-weight: 600; }
.state-row { align-items: center; display: flex; gap: 12px; }
.detail-list { display: grid; gap: 12px; }
.detail-list div { display: grid; gap: 2px; min-width: 0; }
.detail-list span { color: #6b7280; font-size: 12px; text-transform: uppercase; }
.detail-list strong { color: #1f2937; font-size: 14px; font-weight: 600; overflow-wrap: anywhere; }
.help-text { color: #4b5563; font-size: 13px; }
.agent-state { margin-top: -12px; margin-bottom: 16px; }
.result-cell { max-width: 360px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.task-table { overflow-x: auto; }
.task-detail { border: 1px solid #d5d9df; border-radius: 6px; padding: 16px; }
.detail-heading { align-items: center; display: flex; justify-content: space-between; gap: 12px; }
.detail-heading h3 { font-size: 18px; margin: 0; overflow-wrap: anywhere; }
.response-text { padding: 12px; background: #f7f8fa; border-radius: 4px; white-space: pre-wrap; overflow-wrap: anywhere; }
.response-text.history-expanded { max-height: none; }
.codex-terminal { background: #151b25; border-radius: 6px; color: #eff4fc; font-family: Consolas, monospace; font-size: 13px; max-height: 420px; overflow: auto; padding: 16px; }
.codex-terminal.terminal-expanded { max-height: none; }
.terminal-entry + .terminal-entry { border-top: 1px solid #354155; margin-top: 12px; padding-top: 12px; }
.terminal-entry pre { color: inherit; font: inherit; margin: 4px 0 0; overflow-wrap: anywhere; white-space: pre-wrap; }
.terminal-label { color: #8bd3ff; font-weight: bold; }
.terminal-empty { color: #aeb9c7; }
.command-help { background: #eef5ff; border-radius: 4px; padding: 12px; }
.message-bubble { max-width: 92%; margin: 14px 0; padding: 14px 16px; border-radius: 12px; background: #f3f5f8; white-space: pre-wrap; overflow-wrap: anywhere; }
.message-bubble.user { margin-left: auto; background: #e8f1ff; }
.message-bubble strong { display: block; margin-bottom: 6px; }
.activity-entry { border-left: 3px solid #c9d9eb; padding: 7px 12px; margin: 8px 0; white-space: pre-wrap; overflow-wrap: anywhere; }
.technical-entry { background: #f7f8fa; margin: 8px 0; padding: 8px; overflow-wrap: anywhere; }
.technical-entry pre { white-space: pre-wrap; }
.file-row { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; padding: 6px 0; border-bottom: 1px solid #eee; }
.file-row span { flex: 1; overflow-wrap: anywhere; }
@media (max-width: 600px) { .ec2-codex-page { padding: 12px; } .page-header { display: block; } .header-actions { margin-top: 12px; } .header-actions .v-btn, .action-grid .v-btn { width: 100%; } }
</style>
